import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import NorthEastIcon from '@mui/icons-material/NorthEast';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import {
  Box,
  Button,
  Dialog,
  IconButton,
  Link,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useEffect, useRef, useState } from 'react';

import { type ChangelogEntry } from '~/data/schemas/changelog';
import { BUTTON_SX, SURFACE_RADIUS } from '~/theme';

export function WhatsNewShowcaseDialog({
  entries,
  initialIndex = 0,
  autoPlay = false,
  onClose,
  onGuideClick,
}: {
  entries: ChangelogEntry[];
  initialIndex?: number;
  autoPlay?: boolean;
  onClose: (position: number, finished: boolean) => void;
  onGuideClick: (entry: ChangelogEntry) => void;
}) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [index, setIndex] = useState(initialIndex);

  const total = entries.length;
  const position = Math.min(index, total - 1);
  const current = entries[position];
  const isLast = position === total - 1;

  if (!current) return null;

  return (
    <Dialog
      open
      fullScreen={fullScreen}
      maxWidth="md"
      fullWidth
      onClose={() => onClose(position, false)}
      aria-labelledby="whats-new-showcase-title"
      slotProps={{ paper: { sx: { borderRadius: fullScreen ? 0 : `${SURFACE_RADIUS}px` } } }}
    >
      <Box
        sx={{
          p: { xs: 2, sm: 3 },
          pb: { xs: 1.5, sm: 2 },
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.14)} 0%, ${alpha(theme.palette.primary.light, 0.04)} 100%)`,
        }}
      >
        <Stack
          direction="row"
          sx={{ alignItems: 'center', justifyContent: 'space-between', mt: -1, mb: 1 }}
        >
          <Typography variant="overline" color="primary" sx={{ lineHeight: 1.5 }}>
            {total === 1 ? "What's new" : `What's new · ${position + 1} of ${total}`}
          </Typography>
          <IconButton
            aria-label="Close"
            onClick={() => onClose(position, false)}
            size="small"
            sx={{ mr: -1, bgcolor: 'background.paper' }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
        {current.showcase ? (
          <DemoVideo
            key={current.id}
            src={current.showcase.video}
            poster={current.showcase.poster}
            title={current.title}
            autoPlay={autoPlay}
          />
        ) : null}
      </Box>

      <Stack spacing={2} sx={{ px: { xs: 2, sm: 3 }, pt: 2, pb: 1, overflowY: 'auto' }}>
        <Stack spacing={0.5}>
          <Typography id="whats-new-showcase-title" variant="h5" component="h2">
            {current.title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {current.summary}
          </Typography>
        </Stack>

        {current.showcase &&
        (current.showcase.highlights.length > 0 || current.showcase.steps.length > 0) ? (
          <Box
            sx={{
              display: 'grid',
              gap: { xs: 2, sm: 3 },
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            }}
          >
            {current.showcase.highlights.length > 0 ? (
              <Stack spacing={1}>
                <Typography variant="subtitle2">Why it helps</Typography>
                {current.showcase.highlights.map((highlight) => (
                  <Stack
                    key={highlight}
                    direction="row"
                    spacing={1}
                    sx={{ alignItems: 'flex-start' }}
                  >
                    <CheckCircleIcon color="success" sx={{ fontSize: 18, mt: '2px' }} />
                    <Typography variant="body2">{highlight}</Typography>
                  </Stack>
                ))}
              </Stack>
            ) : null}
            {current.showcase.steps.length > 0 ? (
              <Stack spacing={1}>
                <Typography variant="subtitle2">How to start</Typography>
                {current.showcase.steps.map((step, stepIndex) => (
                  <Stack key={step} direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
                    <Box
                      aria-hidden
                      sx={{
                        flexShrink: 0,
                        width: 20,
                        height: 20,
                        mt: '1px',
                        borderRadius: '50%',
                        display: 'grid',
                        placeItems: 'center',
                        fontSize: 12,
                        fontWeight: 600,
                        color: 'primary.contrastText',
                        bgcolor: 'primary.main',
                      }}
                    >
                      {stepIndex + 1}
                    </Box>
                    <Typography variant="body2">{step}</Typography>
                  </Stack>
                ))}
              </Stack>
            ) : null}
          </Box>
        ) : null}
      </Stack>

      <Stack
        direction="row"
        spacing={1}
        sx={{ alignItems: 'center', px: { xs: 2, sm: 3 }, py: 2, mt: 'auto' }}
      >
        {current.url ? (
          <Link
            href={current.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => onGuideClick(current)}
            underline="hover"
            variant="body2"
            sx={{ fontWeight: 600 }}
          >
            Read the full guide
            <NorthEastIcon sx={{ fontSize: '0.8em', ml: 0.5, verticalAlign: '-0.05em' }} />
          </Link>
        ) : null}
        <Box sx={{ flex: 1 }} />
        {position > 0 ? (
          <Button onClick={() => setIndex(position - 1)} sx={BUTTON_SX}>
            Back
          </Button>
        ) : null}
        <Button
          variant="contained"
          onClick={isLast ? () => onClose(position, true) : () => setIndex(position + 1)}
          sx={BUTTON_SX}
        >
          {isLast ? 'Done' : 'Next'}
        </Button>
      </Stack>
    </Dialog>
  );
}

function DemoVideo({
  src,
  poster,
  title,
  autoPlay,
}: {
  src: string;
  poster?: string;
  title: string;
  autoPlay: boolean;
}) {
  const theme = useTheme();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [started, setStarted] = useState(false);
  const [seconds, setSeconds] = useState<number | null>(null);
  const length = seconds === null ? null : formatLength(seconds);

  useEffect(() => {
    if (!autoPlay) return;
    Promise.resolve()
      .then(() => videoRef.current?.play())
      .catch(() => undefined);
  }, [autoPlay]);

  return (
    <Box sx={{ position: 'relative' }}>
      <Box
        ref={videoRef}
        component="video"
        src={src}
        poster={poster}
        playsInline
        controls={started}
        preload="metadata"
        onPlay={() => setStarted(true)}
        onLoadedMetadata={(event) => {
          const { duration } = event.currentTarget;
          if (Number.isFinite(duration) && duration > 0) setSeconds(duration);
        }}
        aria-label={`${title} demo`}
        sx={{
          display: 'block',
          width: '100%',
          aspectRatio: '16 / 10',
          objectFit: 'cover',
          borderRadius: '12px',
          bgcolor: 'grey.900',
          boxShadow: `0 12px 40px ${alpha(theme.palette.primary.dark, 0.25)}`,
        }}
      />
      {started ? null : (
        <Box
          component="button"
          type="button"
          aria-label={`Play the ${title} demo${length ? `, ${length}` : ''}`}
          onClick={() => void videoRef.current?.play().catch(() => undefined)}
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'grid',
            placeItems: 'center',
            border: 0,
            p: 0,
            borderRadius: '12px',
            cursor: 'pointer',
            bgcolor: alpha(theme.palette.common.black, 0.18),
            transition: 'background-color .2s ease',
            '& .play': { transition: 'transform .2s ease' },
            '&:hover': { bgcolor: alpha(theme.palette.common.black, 0.08) },
            '&:hover .play': { transform: 'scale(1.08)' },
            '&:focus-visible': {
              outline: `3px solid ${theme.palette.primary.main}`,
              outlineOffset: 2,
            },
          }}
        >
          <DemoPlayPill length={length} />
        </Box>
      )}
    </Box>
  );
}

export function DemoPlayPill({
  length,
  size = 'large',
}: {
  length: string | null;
  size?: 'large' | 'small';
}) {
  const theme = useTheme();
  const small = size === 'small';

  return (
    <Stack
      className="play"
      direction="row"
      spacing={small ? 1 : 1.5}
      sx={{
        alignItems: 'center',
        p: small ? 0.5 : 0.75,
        pr: small ? 1.75 : 2.5,
        borderRadius: 999,
        bgcolor: 'background.paper',
        boxShadow: `0 8px 24px ${alpha(theme.palette.common.black, 0.3)}`,
      }}
    >
      <Box
        sx={{
          width: small ? 36 : 52,
          height: small ? 36 : 52,
          borderRadius: '50%',
          display: 'grid',
          placeItems: 'center',
          color: 'primary.contrastText',
          bgcolor: 'primary.main',
        }}
      >
        <PlayArrowRoundedIcon sx={{ fontSize: small ? 26 : 36 }} />
      </Box>
      <Box sx={{ textAlign: 'left' }}>
        <Typography
          variant={small ? 'body2' : 'subtitle2'}
          color="text.primary"
          sx={{ fontWeight: 500, lineHeight: 1.3 }}
        >
          Watch the demo
        </Typography>
        {length ? (
          <Typography variant="caption" color="text.secondary" display="block">
            {length}
          </Typography>
        ) : null}
      </Box>
    </Stack>
  );
}

export function formatLength(seconds: number) {
  const total = Math.round(seconds);
  if (total < 60) return `${total} sec`;
  return `${Math.floor(total / 60)} min ${total % 60} sec`;
}
